import React, {useEffect, useRef, useMemo} from 'react';
import type {AstView, EnrichedAstNode, RenderVariantsType} from 'rendition-core/types';
import {usePreviewContext} from 'Contexts/PreviewContext';
import {useNodeCallbackContext} from 'Contexts/NodeCallBackContext';
import {getStyles} from './RichPreviewDiffLogic';

export const RichPreview: React.FC<
    React.PropsWithChildren<{
        view: AstView;
        forceFixedSizing?: boolean;
        isolatedNode?: EnrichedAstNode | null;
        isUserSelectable?: boolean;
        fixedPosition?: boolean;
    }>
> = ({view, isolatedNode, fixedPosition, forceFixedSizing, isUserSelectable}) => {
    // find all nodes that have changed. Pass into GenericNode downward.
    // if has changed trigger rerender else leave.

    return (
        <GenericNodeRender
            forceFixedSizing={forceFixedSizing}
            fixedPosition={fixedPosition}
            isRoot
            node={isolatedNode || view.tree}
            isUserSelectable={isUserSelectable}
        />
    );
};

type NodePropBase = {
    node: EnrichedAstNode;
    fixedPosition?: boolean;
    isRoot?: boolean;
    isTextChild?: boolean;
    forceFixedSizing?: boolean;
    isUserSelectable?: boolean;
    offsetX?: number;
    offsetY?: number;
};

const GenericNodeRender: React.FC<React.PropsWithChildren<NodePropBase>> = ({
    node,
    fixedPosition,
    isRoot,
    isTextChild,
    forceFixedSizing,
    isUserSelectable,
    offsetX = 0,
    offsetY = 0,
}) => {
    const ref = useRef<(HTMLDivElement | HTMLImageElement) | null>(null);
    const {
        view,
        onNodeClick,
        onNodeMouseEnter,
        onNodeMouseLeave,
        hiddenNodes,
        translucentNodes,
        unclickableNodes,
        onNodeRightClick,
    } = usePreviewContext();
    const {addNodeCallback} = useNodeCallbackContext();

    const nodeToBuild: EnrichedAstNode = useMemo(() => {
        if (node.type !== 'Instance') {
            return node;
        }
        const component = (view.meta.complexComponentMap[node.refId].renderVariants as RenderVariantsType).finalTree;
        const instanceCss: {[key: string]: string} = {};

        node.mandatoryProperties.forEach((key) => {
            instanceCss[key] = node.styles[key];
        });
        return {
            ...component,
            children: node.children,
            properties: node.properties,
            internalId: node.internalId,
            position: node.position,
            innerPosition: node.innerPosition,
            styles: {...component.styles, ...instanceCss},
        };
    }, [node, view.meta.complexComponentMap]);

    useEffect(() => {
        if (!ref.current || isTextChild) {
            return;
        }
        const getLocation = () => {
            const rec = ref.current?.getBoundingClientRect();
            if (!rec) {
                return {actual: {x: 0, y: 0, height: 0, width: 0}, simulated: {x: 0, y: 0, height: 0, width: 0}};
            }
            return {
                actual: {x: rec.left, y: rec.top, height: rec.height, width: rec.width},
                simulated: {
                    x: node.position.absoluteX,
                    y: node.position.absoluteY,
                    width: node.position.width,
                    height: node.position.height,
                },
            };
        };

        addNodeCallback(nodeToBuild.internalId, getLocation);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref.current]);

    const commonProps = isTextChild
        ? {
              style: getStyles(
                  nodeToBuild,
                  fixedPosition,
                  offsetX,
                  offsetY,
                  hiddenNodes,
                  translucentNodes,
                  unclickableNodes,
                  !!forceFixedSizing,
                  !!isRoot,
                  !!isUserSelectable
              ),
          }
        : {
              onContextMenu: (e) => {
                  onNodeRightClick(e, node.internalId);
              },
              onMouseLeave: (e) => onNodeMouseLeave(e, nodeToBuild.internalId),
              onMouseMove: (e) => {
                  onNodeMouseEnter(e, nodeToBuild.internalId);
              },
              onMouseDown: (e: React.MouseEvent) => {
                  onNodeClick(e, nodeToBuild.internalId);
              },
              style: getStyles(
                  nodeToBuild,
                  fixedPosition,
                  offsetX,
                  offsetY,
                  hiddenNodes,
                  translucentNodes,
                  unclickableNodes,
                  !!forceFixedSizing,
                  !!isRoot,
                  !!isUserSelectable
              ),
          };

    if (nodeToBuild.type === 'Image') {
        return (
            <img
                src={nodeToBuild?.properties?.src?.value as string}
                ref={ref as React.MutableRefObject<HTMLImageElement | null>}
                alt="preview"
                draggable={false}
                {...commonProps}
            />
        );
    }

    const Tag = isTextChild ? 'span' : 'div'; // TODO test this more.

    return (
        <Tag ref={ref} {...commonProps}>
            {nodeToBuild.type === 'Text' &&
                nodeToBuild.properties['childText']?.type === 'string' &&
                splitTextIntoMultipleLines(
                    nodeToBuild.properties['childText'].value,
                    nodeToBuild.type === 'Text' && nodeToBuild.meta.isListItem
                )}
            {nodeToBuild.children.map((child) => (
                <GenericNodeRender
                    forceFixedSizing={forceFixedSizing}
                    isTextChild={nodeToBuild.type === 'Text'}
                    fixedPosition={fixedPosition}
                    isRoot={false}
                    key={child.internalId}
                    node={child}
                    offsetX={node.position.absoluteX}
                    offsetY={node.position.absoluteY}
                    isUserSelectable={isUserSelectable}
                />
            ))}
        </Tag>
    );
};

const splitTextIntoMultipleLines = (text: string, isListItem?: boolean) => {
    return (
        <>
            {text
                .split('\u2028')
                .join('\n')
                .split(/\r?\n/)
                .map((line, i) => {
                    return (
                        <span key={`${i}-${line}`}>
                            {i !== 0 && <br />}
                            {isListItem && '\u2022 '}
                            {line || '\u00A0'}
                        </span>
                    );
                })}
        </>
    );
};
